import React from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import { map, isEmpty, find, cloneDeep, isEqual, assign, isNaN, filter } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';
import uuid from 'uuid/v1';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';

import {
  Box,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  Divider,
  Stack,
  Modal
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import { renderIconButton } from '../../components/MaterialMUIComponent';
import Loading from '../../components/Loading/index';
import Videos from '../../../api/Videos/Videos';
import Projects from '../../../api/Projects/Projects';
import Profiles from '../../../api/Profiles/Profiles';
import * as profileOps from '../../../api/Profiles/profiles-operations';
import * as videoOps from '../../../api/Videos/video-operations';

import ShowListIconsFontawesome from '../../components/ShowListIconsFontawesome';
import { LIBRARY_ICON_TYPES } from '../../../api/AnnotationTemplates/AnnotationTemplates';
import AnnotationTemplateSettings from '../../components/AnnotationTemplateSettings';

const MapsAdvancedNotesField = props => {
  const {
    allVideos,
    projectList,
    listComponentsAdvanced,
    notes,
    setListComponentsAdvanced,
    setNotes,
    loading,
    profiles
  } = props;
  const [openModal, setOpenModal] = React.useState(false);
  const [openOptions, setOpenOptions] = React.useState('none');
  const [openConfigTemplates, setOpenConfigTemplates] = React.useState(false);

  const [newComponentSchema, setNewComponentSchema] = React.useState(
    listComponentsAdvanced.NotesComponent
  );
  const [newNotesSchema, setNewNotesSchema] = React.useState(notes);
  const [projectSelect, setProjectSelect] = React.useState('All');
  const [videosToShow, setVideoToShow] = React.useState([]);
  const [listToShow, setListToShow] = React.useState([]);

  const renderManyIconButtons = array =>
    map(
      array,
      item =>
        item.isShow &&
        renderIconButton({
          onClick: item.function,
          icon: item.icon,
          titleTooltip: item.title,
          disabled: item.disabled || false,
          style: {
            maxWidth: '38px',
            maxHeight: '38px',
            padding: 0,
            color: item.color || '#2A3F90',
            ...item.style
          }
        })
    );

  const renderCheckBox = ({ checkedProp, onChangeProp, valueSchemaProp, textProp }) => {
    return (
      <>
        <Checkbox
          key={uuid()}
          checked={checkedProp}
          onChange={onChangeProp}
          sx={{
            color: '#2A3F90',
            '&.Mui-checked': {
              color: '#2A3F90'
            }
          }}
          value={valueSchemaProp}
        />
        {textProp}
      </>
    );
  };

  const changeStatesComponent = newValues => {
    setNewComponentSchema({
      ...newComponentSchema,
      ...newValues
    });
    setListComponentsAdvanced({
      ...listComponentsAdvanced,
      NotesComponent: {
        ...listComponentsAdvanced.NotesComponent,
        ...newValues
      }
    });
  };

  const changeStatesNotes = (key, newValue, moreStates = {}) => {
    const newObject = { [key]: newValue, ...moreStates };
    setNewNotesSchema({ ...newNotesSchema, ...newObject });
    setNotes({ ...newNotesSchema, ...newObject });
  };

  const handleIconSelected = (elementIcon, library) => {
    const isSvg = isEqual(library, LIBRARY_ICON_TYPES.SVG);
    const newValues = {
      iconFontawesome: !isSvg ? elementIcon : elementIcon.name,
      libraryIcon: library
    };
    if (isSvg)
      assign(newValues, {
        iconSvg: elementIcon.url,
        cetegorySvg: elementIcon.category
      });
    changeStatesComponent(newValues);
  };

  const handleProject = (project, list) => {
    const newVideosToShow = isEqual(project, 'All')
      ? list
      : list.filter(element => element.video.project === project._id);
    setVideoToShow(newVideosToShow);
    setProjectSelect(project);
  };

  const handleProfile = (profileName, video) => {
    const cloneStateEntWine = cloneDeep(newNotesSchema.entWine);
    if (profileName !== 'none') {
      const profile = find(profiles, ['name', profileName]);
      const newEntWine = {
        name: profileOps.getEntWineName(profile),
        path: profileOps.getPath(profile),
        videoId: profileOps.getVideoId(profile),
        buffer: profileOps.getBuffer(profile),
        clasify: profileOps.getClasify(profile),
        profileId: profileOps.getId(profile)
      };
      changeStatesNotes('entWine', [...newNotesSchema.entWine, newEntWine]);
    } else {
      const videoId = videoOps.getId(video);

      const eliminatedProfile = find(cloneStateEntWine, ['videoId', videoId]);
      if (!isEmpty(eliminatedProfile)) {
        cloneStateEntWine.splice(cloneStateEntWine.indexOf(eliminatedProfile), 1);
      }
      changeStatesNotes('entWine', cloneStateEntWine);
    }
    const newList = map(listToShow, element => {
      const cloneElement = cloneDeep(element);
      if (element.video._id === video._id) {
        cloneElement.profileSelectedNotes = profileName;
      }
      return cloneElement;
    });
    setListToShow(newList);
    handleProject(projectSelect, newList);
  };

  const handleSelectVideo = video => {
    const cloneStateVideos = cloneDeep(newNotesSchema.videos);
    const indexItem = cloneStateVideos.indexOf(
      find(cloneStateVideos, item => isEqual(videoOps.getId(video), item))
    );
    if (isEqual(indexItem, -1)) {
      changeStatesNotes('videos', [...cloneStateVideos, videoOps.getId(video)], {});
    } else {
      cloneStateVideos.splice(indexItem, 1);
      changeStatesNotes('videos', cloneStateVideos, {});
    }
  };

  const optionsVideo = [
    {
      checked: newNotesSchema.showVideo,
      function: event => changeStatesNotes(event.target.value, !newNotesSchema.showVideo),
      value: 'showVideo',
      text: 'Show video'
    }
  ];

  const optionsAnnotations = [
    {
      checked: newNotesSchema.showCreateAnnotation,
      function: event =>
        changeStatesNotes(event.target.value, !newNotesSchema.showCreateAnnotation),
      value: 'showCreateAnnotation',
      text: 'Create annotation '
    },
    {
      checked: newNotesSchema.showUpdateAnnotation,
      function: event =>
        changeStatesNotes(event.target.value, !newNotesSchema.showUpdateAnnotation),
      value: 'showUpdateAnnotation',
      text: 'Edit annotation'
    },
    {
      checked: newNotesSchema.showDeleteAnnotation,
      function: event =>
        changeStatesNotes(event.target.value, !newNotesSchema.showDeleteAnnotation),
      value: 'showDeleteAnnotation',
      text: 'Remove annotation'
    },
    {
      checked: newNotesSchema.showImportAnnotation,
      function: event =>
        changeStatesNotes(event.target.value, !newNotesSchema.showImportAnnotation),
      value: 'showImportAnnotation',
      text: 'Import annotation'
    },
    {
      checked: newNotesSchema.showExportAnnotation,
      function: event =>
        changeStatesNotes(event.target.value, !newNotesSchema.showExportAnnotation),
      value: 'showExportAnnotation',
      text: 'Export annotation'
    },
    {
      checked: newNotesSchema.interval.show,
      function: () =>
        changeStatesNotes('interval', {
          show: !newNotesSchema.interval.show,
          value: newNotesSchema.interval.value
        }),
      value: 'showInterval',
      text: 'Show interval'
    }
  ];

  const geometriesOptions = [
    {
      checked: newNotesSchema.showNotes,
      function: event =>
        changeStatesNotes(event.target.value, !newNotesSchema.showNotes, {
          showNotesDefault: isEqual(!newNotesSchema.showNotes, false)
            ? false
            : newNotesSchema.showNotesDefault
        }),
      value: 'showNotes',
      text: 'Enabled show geometries'
    },
    {
      checked: newNotesSchema.showNotesDefault,
      function: event =>
        changeStatesNotes(event.target.value, !newNotesSchema.showNotesDefault, {
          centerNotesDefault:
            newNotesSchema.centerNotes && isEqual(!newNotesSchema.showNotesDefault, true)
              ? true
              : newNotesSchema.centerNotesDefault
        }),
      value: 'showNotesDefault',
      text: 'Show geometries by default'
    },
    {
      checked: newNotesSchema.centerNotes,
      function: event =>
        changeStatesNotes(event.target.value, !newNotesSchema.centerNotes, {
          centerNotesDefault: isEqual(!newNotesSchema.centerNotes, false)
            ? false
            : newNotesSchema.centerNotesDefault
        }),
      value: 'centerNotes',
      text: 'Enabled Center geometries'
    },
    {
      checked: newNotesSchema.centerNotesDefault,
      function: event => changeStatesNotes(event.target.value, !newNotesSchema.centerNotesDefault),
      value: 'centerNotesDefault',
      text: 'Center by default'
    },
    {
      checked: newNotesSchema.showAnnotations,
      function: event =>
        changeStatesNotes(event.target.value, !newNotesSchema.showAnnotations, {
          showAllNotes: false
        }),
      value: 'showAnnotations',
      text: 'Show annotations'
    },
    {
      checked: newNotesSchema.showTrack,
      function: event =>
        changeStatesNotes(event.target.value, !newNotesSchema.showTrack, {
          showAllTracks: false
        }),
      value: 'showTrack',
      text: 'Show track video'
    },
    {
      checked: newNotesSchema.showAllNotes,
      function: event =>
        changeStatesNotes(event.target.value, !newNotesSchema.showAllNotes, {
          showAnnotations: false
        }),
      value: 'showAllNotes',
      text: 'Show annotations of all videos'
    },
    {
      checked: newNotesSchema.showAllTracks,
      function: event =>
        changeStatesNotes(event.target.value, !newNotesSchema.showAllTracks, {
          showTrack: false
        }),
      value: 'showAllTracks',
      text: 'Show tracks of all videos'
    }
  ];

  const othersOptions = [
    {
      checked: newNotesSchema.showGeomotion,
      function: event => changeStatesNotes(event.target.value, !newNotesSchema.showGeomotion),
      value: 'showGeomotion',
      text: 'Show geomotionVideo'
    },
    {
      checked: newNotesSchema.showGeomotionPublic,
      function: event => changeStatesNotes(event.target.value, !newNotesSchema.showGeomotionPublic),
      value: 'showGeomotionPublic',
      text: 'Show geomotionVideo public '
    },
    {
      checked: newNotesSchema.showStreetView,
      function: event => changeStatesNotes(event.target.value, !newNotesSchema.showStreetView),
      value: 'showStreetView',
      text: 'Show map of StreetView'
    }
  ];

  const secctionsOptions = [
    { secction: 'Video options', options: optionsVideo },
    { secction: 'Annotations options', options: optionsAnnotations },
    { secction: 'Geometries options', options: geometriesOptions },
    { secction: 'Others options', options: othersOptions }
  ];

  React.useEffect(() => {
    if (loading === false && !isEmpty(projectList) && !isEmpty(allVideos)) {
      const list = map(allVideos, item => {
        let stateProfileSelected = { name: 'none' };
        const filterProfiles = profiles.filter(
          element => profileOps.getVideoId(element) === item._id
        );
        if (!isEmpty(newNotesSchema.entWine) && !isEmpty(filterProfiles)) {
          map(newNotesSchema.entWine, element => {
            if (element.videoId === item._id) {
              stateProfileSelected = find(filterProfiles, ['_id', element.profileId]);
              if (isEmpty(stateProfileSelected)) stateProfileSelected = { name: 'none' };
            }
          });
        }
        const object = {
          video: item,
          profiles: filterProfiles,
          profileSelectedNotes: stateProfileSelected.name
        };
        return object;
      });
      setListToShow(list);
      setVideoToShow(list);
    }
  }, [loading]);

  React.useEffect(() => {
    if (!isEmpty(notes)) {
      // eslint-disable-next-line no-prototype-builtins
      if (notes.hasOwnProperty('videosAnnotations')) {
        const cloneNotes = cloneDeep(notes);
        delete cloneNotes.videosAnnotations;
        setNewNotesSchema(cloneNotes);
        setNotes(cloneNotes);
      }
    }
  }, []);
  return (
    <Box display="flex" width="100%">
      <Box display="column" width="50%" gab={1}>
        <Box
          component="form"
          sx={{
            display: 'flex',
            mb: 2,
            width: '100%'
          }}
          noValidate
          autoComplete="off"
        >
          <TextField
            id="outlined-required"
            label="Name component"
            size="small"
            value={newComponentSchema.name}
            onChange={event =>
              setNewComponentSchema({ ...newComponentSchema, name: event.target.value })
            }
            mb={2}
            fullwidth="true"
          />
          <IconButton
            onClick={() => setOpenModal(true)}
            style={{
              color: '#2A3F90'
            }}
          >
            {!isEmpty(newComponentSchema.libraryIcon) ? (
              <>
                {newComponentSchema.libraryIcon === LIBRARY_ICON_TYPES.FONTAWESOME && (
                  <FontAwesomeIcon icon={newComponentSchema.iconFontawesome} />
                )}
                {newComponentSchema.libraryIcon === LIBRARY_ICON_TYPES.SVG && (
                  <img
                    src={newComponentSchema.iconSvg}
                    alt={newComponentSchema.iconFontawesome}
                    style={{ marginRight: '5px', width: '20px', height: '20px' }}
                  />
                )}
              </>
            ) : (
              <TextSnippetOutlinedIcon />
            )}
          </IconButton>
          <IconButton
            onClick={() =>
              setListComponentsAdvanced({
                ...listComponentsAdvanced,
                NotesComponent: newComponentSchema
              })
            }
            style={{
              color: '#2A3F90'
            }}
          >
            <SaveIcon />
          </IconButton>

          <ShowListIconsFontawesome
            openModal={openModal}
            setOpenModal={setOpenModal}
            handleIconSelected={handleIconSelected}
            selectedIcon={listComponentsAdvanced.NotesComponent.iconFontawesome}
            startLibrary={listComponentsAdvanced.NotesComponent.libraryIcon}
            svg={listComponentsAdvanced.NotesComponent.iconSvg}
            startCategorySvg={listComponentsAdvanced.NotesComponent.cetegorySvg}
          />
        </Box>

        {map(secctionsOptions, (item, index) => {
          return (
            <Box key={uuid()} width="100%" marginBottom="5%">
              <Stack key={item.secction} direction="row" width="100%" marginBottom="2%">
                {renderManyIconButtons([
                  {
                    title: 'View more',
                    function: () => {
                      if (!isEqual(openOptions, item.secction)) setOpenOptions(item.secction);
                      else setOpenOptions('none');
                    },
                    icon: !isEqual(openOptions, item.secction) ? (
                      <ExpandMoreIcon />
                    ) : (
                      <ExpandLessIcon />
                    ),
                    isShow: true
                  }
                ])}
                <Typography
                  key={uuid()}
                  variant="body1"
                  sx={{ marginRight: 2, marginBottom: '0%' }}
                >
                  {item.secction}
                </Typography>
                <Divider
                  key={uuid()}
                  sx={{ flexGrow: 1, backgroundColor: 'grey', marginTop: '2%' }}
                />
                {isEqual(index, 1) &&
                  renderManyIconButtons([
                    {
                      title: 'Configuration templates',
                      icon: <SettingsIcon />,
                      function: () => setOpenConfigTemplates(true),
                      isShow: true,
                      style: { marginLeft: '1%' },
                      disabled: isEmpty(newNotesSchema.videos),
                      color: isEmpty(newNotesSchema.videos) ? 'grey' : '#2A3F90'
                    }
                  ])}
              </Stack>

              {isEqual(openOptions, item.secction) &&
                map(item.options, opt => (
                  <Box
                    key={uuid()}
                    display="row"
                    justifyContent="space-evenly"
                    fullwidth="true"
                    mb={2}
                  >
                    {renderCheckBox({
                      checkedProp: opt.checked,
                      onChangeProp: opt.function,
                      valueSchemaProp: opt.value,
                      textProp: opt.text
                    })}
                    {isEqual(opt.value, 'showInterval') && (
                      <TextField
                        key="valueInterval"
                        id="outlined-required"
                        label="Enter a interval"
                        size="small"
                        defaultValue={newNotesSchema.interval.value}
                        style={{ marginLeft: '2%' }}
                        onBlur={event => {
                          if (isNaN(Number(event.target.value)))
                            Bert.alert('Number not valid', 'warning');
                          else
                            changeStatesNotes('interval', {
                              value: Number(event.target.value),
                              show: newNotesSchema.interval.show
                            });
                        }}
                        mb={2}
                      />
                    )}
                  </Box>
                ))}
            </Box>
          );
        })}
      </Box>

      {!loading ? (
        <>
          <Box width="40%" sx={{ m: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="select-project">Project</InputLabel>
              <Select
                labelId="select-project"
                id="select-project"
                label="Project"
                size="small"
                value={projectSelect}
                onChange={event => handleProject(event.target.value, listToShow)}
              >
                <MenuItem value="All">All</MenuItem>
                {projectList.map(item => (
                  <MenuItem key={item.name} value={item}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography>Select videos:</Typography>
            <List style={{ maxHeight: '50vh', overflow: 'auto' }}>
              {!isEmpty(videosToShow) &&
                map(videosToShow, item => (
                  <Box key={uuid()}>
                    <ListItem key={uuid()} button onClick={() => handleSelectVideo(item.video)}>
                      <Checkbox
                        key={item.video._id}
                        edge="start"
                        checked={newNotesSchema.videos.includes(item.video._id)}
                        tabIndex={-1}
                        disableRipple
                        sx={{
                          color: '#2A3F90',
                          '&.Mui-checked': {
                            color: '#2A3F90'
                          }
                        }}
                      />
                      <ListItemText key={uuid()} primary={item.video.name} />
                    </ListItem>
                    {!isEmpty(item.video.track) && !isEmpty(item.profiles) && (
                      <>
                        <ListItem>
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel id="select-perfil">Perfil</InputLabel>
                            <Select
                              labelId="select-perfil"
                              id="select-perfil"
                              label="Perfil"
                              size="small"
                              value={item.profileSelectedNotes}
                              onChange={event => handleProfile(event.target.value, item.video)}
                            >
                              <MenuItem value="none">--------</MenuItem>
                              {item.profiles.map(profile => (
                                <MenuItem key={profile.name} value={profile.name}>
                                  {profile.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </ListItem>
                      </>
                    )}
                  </Box>
                ))}
            </List>
          </Box>
          <Modal open={openConfigTemplates} onClose={() => setOpenConfigTemplates(false)}>
            <Box
              style={{
                position: 'absolute',
                backgroundColor: 'white',
                height: '70%',
                width: '70%',
                top: '15%',
                left: '15%',
                paddingTop: '1%',
                paddingRight: '1%',
                paddingLeft: '1%',
                paddingBottom: '1%'
              }}
            >
              <Box width="100%" height="100%">
                <AnnotationTemplateSettings
                  setClose={setOpenConfigTemplates}
                  videos={filter(allVideos, vid =>
                    newNotesSchema.videos.includes(videoOps.getId(vid))
                  )}
                  envSettings="NotesComponent"
                />
              </Box>
            </Box>
          </Modal>
        </>
      ) : (
        <Loading />
      )}
    </Box>
  );
};

export default withTracker(() => {
  const options = { deleted: { $eq: 'no' } };
  const sortOptions = { sort: { name: 1 } };
  const videosSub = Meteor.isClient && Meteor.subscribe('videos.maps');
  const projectsSub = Meteor.subscribe('projects');
  const profilesSub = Meteor.isClient && Meteor.subscribe('profiles');

  return {
    loading: !videosSub.ready() || !projectsSub.ready() || !profilesSub.ready(),
    allVideos: Videos.find(
      {},
      {
        sort: { name: 1 },
        fields: { owner: 0, createdAt: 0, updatedAt: 0, deleted: 0, shared: 0, audience: 0 }
      }
    ).fetch(),
    projectList: Projects.find(options, sortOptions).fetch(),
    profiles: Profiles.find({}, { sort: { name: 1 } }).fetch()
  };
})(MapsAdvancedNotesField);

MapsAdvancedNotesField.defaultProps = {
  projectList: [],
  loading: true,
  profiles: []
};
MapsAdvancedNotesField.propTypes = {
  notes: PropTypes.object.isRequired,
  setNotes: PropTypes.func.isRequired,
  allVideos: PropTypes.array.isRequired,
  projectList: PropTypes.array,
  profiles: PropTypes.array,
  listComponentsAdvanced: PropTypes.object.isRequired,
  setListComponentsAdvanced: PropTypes.func.isRequired,
  loading: PropTypes.bool
};
